import { useEffect, useState } from 'react';

import InfoIcon from '@mui/icons-material/Info';
import { Box, Button, Link, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { AppSuiteSearchableDropDown } from 'common/components/AppSuiteSearchableDropDown';
import { FileUpload } from 'common/components/FileUpload';
import { Page } from 'common/components/Page';
import { TenantSearchableDropDown } from 'common/components/TenantSearchableDropDown';
import { TooltipIcon } from 'common/components/TooltipIcon';
import {
    IMPORTS_ACCEPTABLE_DATE_FORMATS,
    IMPORTS_DOCUMENTATION_TOOLTIP,
    IMPORTS_DOCUMENTATION_URL,
    IMPORTS_STAY_ON_PAGE_WARNING
} from 'common/constants/admin';
import { DEFAULT_TENANT } from 'common/constants/reports';
import { Routes, RoutesNameMap } from 'common/constants/routes';
import { ApplicationSuite, AppSuiteTenant, ImportType } from 'common/types/Entities';
import { ImportTypesSearchableDropDown } from 'features/imports/common/ImportTypesSearchableDropDown';
import { selectIsTenantDropDownDisabled, setTenantDropDownDisabled } from 'features/reports/tenantSearchableDropDownSlice';
import { useFeatures } from 'flagged';

import { ImportFooterButtons } from './common/ImportFooterButtons';
import { ImportsDialog } from './common/ImportsDialog';
import { allRequiredFieldForImportSet, displayAppSuite, displayTenant, getTemplatesDetails,getTemplateUrl } from './utils/ImportsUtils';

export function Imports() {
    const [importType, setImportType] = useState<ImportType | null>(null);
    const [appSuite, setAppSuite] = useState<ApplicationSuite | null>(null);
    const [tenant, setTenant] = useState<AppSuiteTenant | null>(null);
    const [importFileName, setImportFileName] = useState<string | null>(null);
    const [importFile, setImportFile] = useState<File | null>(null);
    const [importButtonDisabled, setImportButtonDisabled] = useState<boolean>(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const theme = useTheme();
    const dispatch = useAppDispatch();
    const isTenantSearchableDropDownDisabled = useAppSelector(selectIsTenantDropDownDisabled);

    const { userImport, userGroupMembershipsImport } = useFeatures();

    useEffect(() => {
        dispatch(setTenantDropDownDisabled(true));
    }, []);

    useEffect(() => {
        setImportButtonDisabled(!allRequiredFieldForImportSet(importType, appSuite, tenant, importFileName, importFile));
    }, [importType, appSuite, tenant, importFileName, importFile]);

    useEffect(() => {
        if (appSuite?.id) {
            dispatch(setTenantDropDownDisabled(false));
            setTenant(DEFAULT_TENANT);
        }
    }, [appSuite?.id]);

    const handleImportTypeChange = (_e: unknown, newImportType: ImportType | null) => {
        setImportType(newImportType);
    };

    const handleApplicationSuiteChange = (_e: unknown, newAppSuite: ApplicationSuite | null) => {
        setAppSuite(newAppSuite);
    };

    const handleTenantChange = (_e: unknown, newTenant: AppSuiteTenant | null) => {
        setTenant(newTenant);
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setImportFileName(file.name);
            setImportFile(file);
        }
    };

    const handleImportDialogClose = () => {
        setIsDialogOpen(false);
        setImportFileName(null);
        setImportFile(null);
    };

    const getSupportedTemplates = () : string[] => {
        const supportedTemplates = ['ACL Entries'];
        if(userImport){
            supportedTemplates.push('Users');
        }

        if(userGroupMembershipsImport){
            supportedTemplates.push('User Group Memberships');
        }

        return supportedTemplates;
    };

    const getSupportedTemplatesDetails = () => {
        return getTemplatesDetails().filter(t => getSupportedTemplates().some(s => s.toLowerCase() == t.templateName.toLowerCase()));
    };

    return (
        <Box sx={{ display: 'flex' }}>
            <Box sx={{ width: '70%', textAlign: 'left' }}>
                <Page
                    title={RoutesNameMap.get(Routes.Imports)}
                    header={(
                        <Box display='flex' width='50%'>
                            <Typography variant="h5" gutterBottom component="div" marginBottom={'1rem'} marginTop={'1rem'} >
                                {RoutesNameMap.get(Routes.Imports)}
                            </Typography>
                            <Button
                                sx={{
                                    padding: '4px',
                                    minWidth: 'auto',
                                    marginLeft: 'auto',
                                    border: null,
                                    backgroundColor: 'transparent',
                                    alignSelf: 'center'
                                }}
                                target='_blank'
                                href={IMPORTS_DOCUMENTATION_URL}
                            >
                                <TooltipIcon
                                    IconComponent={InfoIcon}
                                    tooltipTitle={IMPORTS_DOCUMENTATION_TOOLTIP}
                                    iconColor='primary'
                                    iconInlineStyles={{ color: theme.palette.primary.accent as string }}
                                />
                            </Button>
                        </Box>
                    )}
                >
                    <Stack
                        sx={{ paddingTop: '1rem', width: '50%' }}
                        spacing={2}
                    >
                        <ImportTypesSearchableDropDown
                            value={importType}
                            supportedTemplates = {getSupportedTemplates()}
                            onChange={handleImportTypeChange} />

                        {displayAppSuite(importType) && <AppSuiteSearchableDropDown
                            value={appSuite}
                            onChange={handleApplicationSuiteChange}
                        />
                        }

                        {displayTenant(importType) && <TenantSearchableDropDown
                            appSuite={appSuite}
                            value={tenant ?? DEFAULT_TENANT}
                            onChange={handleTenantChange}
                            disabled={isTenantSearchableDropDownDisabled}
                        />
                        }

                        <FileUpload
                            fileName={importFileName}
                            onChange={handleFileChange}
                        />

                        <ImportFooterButtons
                            handleOnClickImport={() => setIsDialogOpen(true)}
                            disableImportButton={importButtonDisabled}
                        />

                        <Typography variant={'subtitle2'}>
                            {IMPORTS_ACCEPTABLE_DATE_FORMATS}
                        </Typography>

                        <Typography variant={'subtitle2'}>
                            {IMPORTS_STAY_ON_PAGE_WARNING}
                        </Typography>

                    </Stack>

                    {isDialogOpen &&
                        <ImportsDialog
                            importFile={importFile}
                            importFileName={importFileName}
                            isOpen={isDialogOpen}
                            tenant={tenant}
                            appSuite={appSuite}
                            importType={importType}
                            handleClose={handleImportDialogClose}
                        />
                    }
                </Page>

            </Box>

            <Box sx={{ width: '30%', overflowY: 'auto' }}>
                <Page
                    title={'Templates'}
                    header={(
                        <Typography variant="h5" gutterBottom component="div" marginBottom={'1rem'} marginTop={'1rem'}>
                            {'Templates'}
                        </Typography>
                    )}
                ></Page>

                <Stack
                    alignItems="flex-start"
                    sx={{ paddingLeft: '0.75rem', width: '50%' }}
                    spacing={2}>

                    {getSupportedTemplatesDetails().map((template) => (
                        <Link
                            key={template.templateFileName}
                            href={getTemplateUrl(template.templateFileName)}
                            download={template.templateFileName}
                            underline="hover"
                            sx={{ color: 'primary.accent' }}
                        >
                            {template.templateName}
                        </Link>
                    ))}
                </Stack>

            </Box>
        </Box>
    );
}
